import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import { buildCurriculumLessonMap, buildNeedsReviewStudentGroups } from "./tutorWorkflowData";
import { getAuthUserHeader } from "../utils/userRoles";
import "./TutorNeedsReviewPage.css";

const API = API_BASE_URL;

export default function TutorNeedsReviewPage() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedStudentIds, setExpandedStudentIds] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [dashboardRes, curriculumRes] = await Promise.all([
          fetch(`${API}/tutor/dashboard`, {
            cache: "no-store",
            headers: { "X-User": getAuthUserHeader() },
          }),
          fetch(`${API}/curriculum`, { cache: "no-store" }),
        ]);
        const [dashboardJson, curriculumJson] = await Promise.all([
          dashboardRes.json().catch(() => null),
          curriculumRes.json().catch(() => null),
        ]);
        if (cancelled) return;
        if (!dashboardRes.ok) throw new Error(dashboardJson?.detail || "Unable to load tutor queue.");
        setStudents(Array.isArray(dashboardJson?.students) ? dashboardJson.students : []);
        setCurriculum(curriculumJson || null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load tutor queue.");
          setStudents([]);
          setCurriculum(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const curriculumMap = useMemo(() => buildCurriculumLessonMap(curriculum), [curriculum]);
  const studentGroups = useMemo(() => buildNeedsReviewStudentGroups(students, curriculumMap), [students, curriculumMap]);

  const toggleExpanded = (studentId) => {
    setExpandedStudentIds((current) =>
      current.includes(studentId) ? current.filter((id) => id !== studentId) : [...current, studentId]
    );
  };

  return (
    <section className="tnr-page">
      <header className="tnr-header">
        <div>
          <div className="tnr-eyebrow">Needs Review</div>
          <h1 className="tnr-title">Student triage table</h1>
          <div className="tnr-subtitle">
            {loading ? "Loading students..." : `${studentGroups.length} students need tutor attention`}
          </div>
        </div>
      </header>

      {error ? <div className="tnr-error">{error}</div> : null}

      <div className="tnr-table" role="table" aria-label="Student triage table">
        <div className="tnr-head" role="row">
          <span role="columnheader">Student</span>
          <span role="columnheader">Need</span>
          <span role="columnheader">Latest</span>
          <span role="columnheader">Score</span>
          <span role="columnheader">Action</span>
          <span role="columnheader">Toggle</span>
        </div>

        {studentGroups.map((group) => {
          const expanded = expandedStudentIds.includes(group.student_id);
          return (
            <div key={group.student_id} className="tnr-student-group">
              <button
                type="button"
                className="tnr-row"
                onClick={() => toggleExpanded(group.student_id)}
                aria-expanded={expanded}
                aria-controls={`student-group-${group.student_id}`}
                aria-label={`${group.student_name}, ${group.lesson_count_label}, latest ${group.latest_lesson_label}`}
              >
                <span className="tnr-cell tnr-student" data-label="Student">
                  <span className="tnr-name">{group.student_name}</span>
                  <span className="tnr-id">{group.student_id}</span>
                </span>

                <span className="tnr-cell tnr-need" data-label="Need">
                  <span className="tnr-need-count">{group.lesson_count_label}</span>
                  <span className="tnr-main-reason">{group.highest_priority_reason}</span>
                </span>

                <span className="tnr-cell tnr-latest" data-label="Latest">
                  <span className="tnr-latest-id">{group.latest_lesson_label}</span>
                  <span className="tnr-latest-title">{group.latest_lesson_title || "No lesson title"}</span>
                </span>

                <span className="tnr-cell tnr-score" data-label="Score">
                  {group.highest_priority_item?.score_label || "—"}
                </span>

                <span className="tnr-cell tnr-action" data-label="Action">
                  {group.highest_priority_action || "Open"}
                </span>

                <span className="tnr-cell tnr-toggle" data-label="Toggle">
                  {expanded ? "Close" : "Open"}
                </span>
              </button>

              {expanded ? (
                <div className="tnr-lesson-panel" id={`student-group-${group.student_id}`}>
                  <div className="tnr-lesson-panel-title">
                    {group.student_name} — {group.lesson_count_label}
                  </div>

                  <div className="tnr-lesson-list">
                    {group.lessons.map((item) => (
                      <div key={`${item.student_id}-${item.lesson_id}-${item.created_at || item.priority}`} className="tnr-lesson-item">
                        <div className="tnr-lesson-item-main">
                          <div className="tnr-lesson-line">
                            <span className="tnr-lesson-id">{item.lesson_id}</span>
                            <span className="tnr-lesson-title">{item.lesson_title || "No lesson title"}</span>
                          </div>
                          <div className="tnr-lesson-reason">Reason: {item.reason}</div>
                        </div>

                        <div className="tnr-lesson-meta">
                          <div><span className="tnr-lesson-label">Score:</span> {item.score_label || "—"}</div>
                          <div><span className="tnr-lesson-label">Age:</span> {item.age_label || "—"}</div>
                          <div className="tnr-lesson-action-row">
                            <span className="tnr-lesson-label">Action:</span>
                            <button
                              type="button"
                              className={`tnr-action-button${item.action_label === "Done" ? " done" : ""}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/tutor/review/${item.student_id}/week/${item.week}/lesson/${item.lesson}`);
                              }}
                            >
                              {item.action_label || "Review"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {!loading && !studentGroups.length ? (
        <div className="tnr-empty">No students currently need tutor attention.</div>
      ) : null}
    </section>
  );
}
